// SettingsUserNamePane.jsx

import React, { useEffect, useState } from 'react';
import { updateProfile } from 'firebase/auth';

// Firebase
import { auth } from 'firebaseConfig';

// Context
import { useUser } from 'context/UserContext';

// Hooks
import { useIsMobile } from 'hooks/useDeviceDetect';

// Services
import { updateNode } from 'services/nodes/NodeWriteService';

// Utilities
import { isValidUserName } from 'utilities/Validators';

// Components
import Button from 'components/button/Button';
import TextInput from 'components/textinput/TextInput';

/**
 * SettingsUserNamePane
 *
 * Renders an editable field for the current user's display name.
 *
 * @param {object} theme - Active theme tokens for dynamic styling.
 * @param {object} user - Current signed-in user model.
 *
 * @returns {ReactNode} The rendered user name settings pane.
 */
const SettingsUserNamePane = React.memo(({
    theme,
    user,
}) => {

    // ----- CONTEXT ----- //

    // User Context
    const { setUser } = useUser();

    // ----- HOOKS ----- //

    // Device breakpoint detection.
    const isMobile = useIsMobile();

    // ----- STATE ----- //

    // Controlled input value.
    const [nameValue, setNameValue] = useState(user?.data_profile?.name || user?.title || user?.displayName || '');

    // Save in-flight state.
    const [isSaving, setIsSaving] = useState(false);

    // Error message state.
    const [errorMessage, setErrorMessage] = useState('');

    // Status message state.
    const [statusMessage, setStatusMessage] = useState('');

    // ----- EFFECTS ----- //

    // Keep the input synced to the latest stored display name.
    useEffect(() => {
        setNameValue(user?.data_profile?.name || user?.title || user?.displayName || '');
    }, [user?.data_profile?.name, user?.displayName, user?.title]);

    // ----- MEMOS ----- //

    // Current persisted display name.
    const persistedName = String(user?.data_profile?.name || user?.title || user?.displayName || '').trim();

    // Whether the value differs from the persisted display name.
    const hasChanges = nameValue.trim() !== persistedName;

    // Whether the entered name satisfies signup constraints.
    const isNameValid = isValidUserName(nameValue);

    // ----- HANDLERS ----- //

    /**
     * Updates controlled input value and clears stale messages.
     *
     * @param {object} event - Native input change event.
     */
    const handleChange = (event) => {
        setNameValue(event.target.value);
        setErrorMessage('');
        setStatusMessage('');
    };

    /**
     * Persists the new user display name via the user node.
     */
    const handleSave = async () => {
        if (isSaving) return;

        const userKey = String(user?.key || user?.userKey || '').trim();
        const trimmed = nameValue.trim();

        if (!userKey) {
            setErrorMessage('Unable to update your name right now.');
            return;
        }

        if (!isValidUserName(trimmed)) {
            setErrorMessage('Name is required.');
            return;
        }

        setErrorMessage('');
        setStatusMessage('Saving...');
        setIsSaving(true);

        try {
            await updateNode(userKey, {
                'data_profile.name': trimmed,
                'user.name': trimmed,
                'user.title': trimmed,
                title: trimmed,
            }, { actor: user });

            if (auth.currentUser && auth.currentUser.uid === userKey) {
                try {
                    await updateProfile(auth.currentUser, { displayName: trimmed });
                } catch (profileError) {
                    console.warn('[SettingsUserNamePane] Failed to sync auth display name:', profileError);
                }
            }

            setUser((previous) => {
                if (!previous || (previous.key !== userKey && previous.userKey !== userKey)) return previous;

                return {
                    ...previous,
                    displayName: trimmed,
                    name: trimmed,
                    title: trimmed,
                    data_profile: {
                        ...(previous.data_profile || {}),
                        name: trimmed,
                    },
                    user: {
                        ...(previous.user || {}),
                        name: trimmed,
                        title: trimmed,
                    },
                };
            });

            setStatusMessage('Name updated.');
        } catch (error) {
            setErrorMessage(error?.message || 'Failed to update your name.');
            setStatusMessage('');
        } finally {
            setIsSaving(false);
        }
    };

    // ----- RENDER ----- //

    return (
        <>
            <div
                className="settings-modal-pane-header"
                style={{
                    padding: isMobile ? '6px' : '0px',
                }}
            >
                <div className="settings-modal-pane-header-left">
                    <div className="settings-modal-pane-title" style={{ color: theme.foreground }}>
                        My Name
                    </div>
                    <div className="settings-modal-pane-subtitle" style={{ color: theme.foregroundMuted }}>
                        Update the display name shown on your account.
                    </div>
                </div>
            </div>

            <div className="settings-modal-pane-contents">
                <div
                    className="settings-modal-name-card"
                    style={{
                        borderColor: theme.backgroundHover,
                    }}
                >
                    <TextInput
                        placeholder="Name..."
                        value={nameValue}
                        width="100%"
                        error={Boolean(errorMessage)}
                        helperText={errorMessage}
                        disabled={isSaving}
                        required={true}
                        onChange={handleChange}
                        onEnterPress={hasChanges && isNameValid ? handleSave : undefined}
                        type="text"
                    />

                    <Button
                        iconKey="save"
                        label="Save"
                        enabled={hasChanges && isNameValid && !isSaving}
                        onClick={handleSave}
                    />
                </div>

                {statusMessage && !errorMessage && (
                    <div className="settings-modal-status">{statusMessage}</div>
                )}
            </div>
        </>
    );
});

// Dev Tools
SettingsUserNamePane.displayName = 'SettingsUserNamePane';

export default SettingsUserNamePane;